import React from 'react';
import { default as ImageGallery } from 'react-image-gallery';
import { Asset } from '../../../../../common/src/domain/asset/assetTableSchema';
import { PartialAndUndefined } from '../../../../../common/src/utils/PartialAndUndefined';
import { useTranslate } from '../../../mixins/translate/translate';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import { PanelButtonLink } from '../../elements';
import { showAssetInNewWindow } from '../../utils/showAssetInNewWindow';

type AssetForViewer = PartialAndUndefined<Pick<Asset, 'description' | 'filenameThumbnail' | 'filenameMidSize' | 'name'>> & Pick<Asset, 'id' | 'type'>;

// eslint-disable-next-line @typescript-eslint/naming-convention
export function AssetsViewer(props: { assets: AssetForViewer[]; surveyId?: undefined | string }) {
    const translate = useTranslate();
    const [startIndexGallery, setStartIndexGallery] = React.useState<number>(0);
    const [isGalleryVisible, setGalleryVisible] = React.useState<boolean>(false);
    const [columnViewSize, setColumnViewSize] = React.useState<number>(48);
    const [assetsInColumnView, setAssetsInColumnView] = React.useState<AssetForViewer[]>([]);

    let imageGallery: ImageGallery | null;
    const loader = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const options = {
            root: null as any,
            rootMargin: '20px',
            threshold: 1.0,
        };
        // initialize IntersectionObserver and attaching to Load More div. Does not work with IE10
        try {
            const observer = new IntersectionObserver(handleObserver, options);
            if (loader.current) {
                observer.observe(loader.current);
            }
        } catch {
            console.warn('Coud not create IntersectionObserver');
        }
    }, []);

    React.useEffect(() => {
        setAssetsInColumnView(props.assets.slice(0, columnViewSize));
    }, [columnViewSize, props.assets]);

    const handleObserver = (entities: any) => {
        const target = entities[0];
        if (target.isIntersecting) {
            setColumnViewSize((size) => size + 48);
        }
    };

    const images = props.assets
        .filter((asset) => asset.type === assetTypes.Image || asset.type === assetTypes.DerivedImage || asset.type === assetTypes.Image360)
        .map((item) => ({
            id: item.id,
            original: item.filenameMidSize ? `/api/assets/${item.id}/download?midSize=true` : `/api/assets/${item.id}/download`,
            thumbnail: `/api/assets/${item.id}/download?thumb=true`,
            description: item.description,
        }));

    const onImageClick = () => {
        if (imageGallery) {
            const image = images[imageGallery.getCurrentIndex()]!;
            const assetSelected = props.assets.find((item) => item.id === image.id);
            if (assetSelected) {
                showAssetInNewWindow(assetSelected, { surveyId: props.surveyId });
            }
        }
    };

    const showAsset = (asset: AssetForViewer) => {
        if ((asset.type === assetTypes.Image || asset.type === assetTypes.DerivedImage || asset.type === assetTypes.Image360) && images.length > 1) {
            const index = images.findIndex((image) => image.id === asset.id);
            setStartIndexGallery(index);
            setGalleryVisible(true);
        } else {
            showAssetInNewWindow(asset, { surveyId: props.surveyId });
        }
    };

    const getAssetThumbnailUrl = (asset: AssetForViewer) => {
        if (asset.type === assetTypes.MeasurementXLS) {
            return '/assets/img/excel_thmb.png';
        }
        if (asset.type === assetTypes.MeasurementPDF) {
            if (asset.filenameThumbnail) {
                return `/api/assets/${asset.id}/download?thumb=true`;
            }
            return '/assets/img/pdf_thmb.png';
        }
        if (asset.type === assetTypes.Image || asset.type === assetTypes.Image360 || asset.type === assetTypes.ImageTif) {
            if (asset.filenameThumbnail) {
                return `/api/assets/${asset.id}/download?thumb=true`;
            }
            return '/assets/img/image_thmb.png';
        }
        return `/api/assets/${asset.id}/download?thumb=true`;
    };

    return (
        <>
            {images.length > 1 && (
                <>
                    <div className="text-right">
                        {(isGalleryVisible && (
                            <PanelButtonLink
                                onClick={() => setGalleryVisible(false)}
                                text={translate('asset.showColumns')}
                                icon="fa fa-th-large"
                                xsmall={true}
                                primary={true}
                            />
                        )) || (
                            <PanelButtonLink
                                onClick={() => setGalleryVisible(true)}
                                text={translate('asset.showGallery')}
                                icon="fa fa-picture-o"
                                xsmall={true}
                            />
                        )}
                    </div>
                </>
            )}

            <div className="row">
                {(isGalleryVisible && (
                    <div className="p10" style={{ minHeight: '500px', cursor: 'pointer' }}>
                        <ImageGallery
                            lazyLoad={images.length > 20}
                            items={images.map((image) => ({ ...image, description: image.description ?? '' }))}
                            startIndex={startIndexGallery}
                            ref={(i) => {
                                imageGallery = i;
                            }}
                            showThumbnails={false}
                            showBullets={images.length < 20}
                            showIndex={true}
                            showPlayButton={false}
                            onClick={() => onImageClick()}
                        />
                    </div>
                )) || (
                    <>
                        {assetsInColumnView &&
                            assetsInColumnView.map((asset) => (
                                <div className="col-sm-6 col-md-3" key={asset.id}>
                                    <div className="thumbnail">
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                showAsset(asset);
                                            }}
                                            className={`${asset.type}-report`}
                                        >
                                            <img
                                                loading="lazy"
                                                src={getAssetThumbnailUrl(asset)}
                                                height="120px"
                                                title={asset.name}
                                                className="br-a"
                                            />
                                        </a>
                                        <div
                                            className="caption"
                                            title={asset.description ?? undefined}
                                            style={{ minHeight: '80px', maxHeight: '80px', overflow: 'hidden', overflowX: 'hidden' }}
                                        >
                                            {asset.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </>
                )}
            </div>
            {!isGalleryVisible && columnViewSize < props.assets.length && loader && (
                <div className="row">
                    <div className="loading" ref={loader}>
                        Load more...
                    </div>
                </div>
            )}
        </>
    );
}
